import {FC, useState} from 'react'
import {useAuth} from '../../modules/auth'
import {api, ApiError} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from '../../components/PageHead'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const Card: FC<{title: string; sub?: string; children: React.ReactNode}> = ({title, sub, children}) => (
  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', padding: '24px 26px', marginBottom: 18}}>
    <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>{title}</h2>
    {sub && <p style={{fontSize: 13.5, color: MUTED, margin: '4px 0 18px'}}>{sub}</p>}
    {!sub && <div style={{height: 16}} />}
    {children}
  </div>
)

const Notice: FC<{kind: 'ok' | 'err'; children: React.ReactNode}> = ({kind, children}) => (
  <div style={{borderRadius: 9, padding: '10px 14px', fontSize: 14, marginBottom: 16, ...(kind === 'ok' ? {background: 'rgba(3,149,64,0.1)', color: '#1f9d5b'} : {background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626'})}}>
    {children}
  </div>
)

const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)

const btn = (disabled: boolean): React.CSSProperties => ({
  fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none',
  borderRadius: 10, padding: '12px 26px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
})

const SettingsPage: FC = () => {
  const {currentUser, setCurrentUser} = useAuth()

  // Profile form
  const [firstName, setFirstName] = useState(currentUser?.first_name ?? '')
  const [lastName, setLastName] = useState(currentUser?.last_name ?? '')
  const [phone, setPhone] = useState(currentUser?.phone ?? '')
  const [pSaving, setPSaving] = useState(false)
  const [pMsg, setPMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  // Password form
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pwSaving, setPwSaving] = useState(false)
  const [pwMsg, setPwMsg] = useState<{kind: 'ok' | 'err'; text: string} | null>(null)

  const saveProfile = async () => {
    setPSaving(true)
    setPMsg(null)
    try {
      const updated = await api.updateMyProfile({firstName, lastName, phone: phone.trim() || null})
      setCurrentUser((u) => (u ? {...u, first_name: updated.firstName, last_name: updated.lastName, phone: updated.phone ?? undefined} : u))
      setPMsg({kind: 'ok', text: 'Profile updated.'})
    } catch (e) {
      setPMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not save profile'})
    } finally {
      setPSaving(false)
    }
  }

  const changePassword = async () => {
    setPwMsg(null)
    if (next.length < 8) return setPwMsg({kind: 'err', text: 'New password must be at least 8 characters.'})
    if (next !== confirm) return setPwMsg({kind: 'err', text: 'New passwords do not match.'})
    setPwSaving(true)
    try {
      await api.changeMyPassword(current, next)
      setCurrent('')
      setNext('')
      setConfirm('')
      setPwMsg({kind: 'ok', text: 'Password changed.'})
    } catch (e) {
      setPwMsg({kind: 'err', text: e instanceof ApiError ? e.message : 'Could not change password'})
    } finally {
      setPwSaving(false)
    }
  }

  return (
    <div>
      <PageHead icon='⚙️' eyebrow='Account' title='Settings' sub='Manage your admin details and password.' />

      <div className='ccp-stagger'>
        <Card title='Profile details' sub='Your name and contact info.'>
          {pMsg && <Notice kind={pMsg.kind}>{pMsg.text}</Notice>}
          <div className='row g-3'>
            <div className='col-md-6'>
              <Label>First name</Label>
              <input style={inputStyle} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className='col-md-6'>
              <Label>Last name</Label>
              <input style={inputStyle} value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className='col-md-6'>
              <Label>Email</Label>
              <input style={{...inputStyle, background: '#f4f6fa', color: MUTED}} value={currentUser?.email ?? ''} disabled />
            </div>
            <div className='col-md-6'>
              <Label>Phone</Label>
              <input style={inputStyle} value={phone} placeholder='Optional' onChange={(e) => setPhone(e.target.value)} />
            </div>
          </div>
          <div style={{marginTop: 20}}>
            <button onClick={saveProfile} disabled={pSaving || !firstName.trim() || !lastName.trim()} style={btn(pSaving || !firstName.trim() || !lastName.trim())}>
              {pSaving ? <Spinner size={14} /> : 'Save changes'}
            </button>
          </div>
        </Card>

        <Card title='Change password' sub='Use a strong password (at least 8 characters).'>
          {pwMsg && <Notice kind={pwMsg.kind}>{pwMsg.text}</Notice>}
          <div className='row g-3'>
            <div className='col-md-4'>
              <Label>Current password</Label>
              <input type='password' autoComplete='current-password' style={inputStyle} value={current} onChange={(e) => setCurrent(e.target.value)} />
            </div>
            <div className='col-md-4'>
              <Label>New password</Label>
              <input type='password' autoComplete='new-password' style={inputStyle} value={next} onChange={(e) => setNext(e.target.value)} />
            </div>
            <div className='col-md-4'>
              <Label>Confirm new</Label>
              <input type='password' autoComplete='new-password' style={inputStyle} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </div>
          <div style={{marginTop: 20}}>
            <button onClick={changePassword} disabled={pwSaving || !current || !next || !confirm} style={btn(pwSaving || !current || !next || !confirm)}>
              {pwSaving ? <Spinner size={14} /> : 'Update password'}
            </button>
          </div>
        </Card>
      </div>
    </div>
  )
}

export {SettingsPage}
